import { ExclusionReasonCount, NO_REASON_FILTER_PARAM } from "@signalist/shared-types";

import { InclusionFilterStatus } from "@/enum/list.enum";

import { List } from "../../../schema/list";
import { canonicalReasonParam, getReasonLabel, toCount } from "../../../utils/exclusionReasonMeta";
import { getInclusionFilterOptions } from "./getInclusionFilterOptions";
import {
	getSelectedReasons,
	GroupedShowSelection,
	isBucketFullySelected,
} from "./groupedShowSelection";

/** One reason sub-row of a bucket in the grouped Show dropdown. */
export interface GroupedShowReason {
	filterParam: string;
	label: string;
	count: number;
}

/** One bucket row (Found / Unsure / Not found / Blacklist) with its reason sub-rows. */
export interface GroupedShowBucket {
	status: InclusionFilterStatus;
	title: string;
	information?: string;
	count: number;
	reasons: GroupedShowReason[];
}

/**
 * Biggest reason first, with the catch-all "Other" pinned to the bottom (PREC-3871). The API
 * returns the rows in GROUP BY order, which is arbitrary — and "Other" reads as a remainder, so
 * it belongs after the named reasons however large it happens to be.
 */
const byProminence = (a: GroupedShowReason, b: GroupedShowReason): number => {
	if ((a.filterParam === NO_REASON_FILTER_PARAM) !== (b.filterParam === NO_REASON_FILTER_PARAM)) {
		return a.filterParam === NO_REASON_FILTER_PARAM ? 1 : -1;
	}

	return b.count - a.count;
};

/**
 * A bucket whose only child is the catch-all renders no children at all (PREC-4083): the children
 * are a *breakdown*, and breaking a bucket down into itself says nothing while reading as though
 * something is missing. It is the normal shape for Found (no lead there has a reason) and, until
 * every blacklist verdict has been recorded, for Blacklist.
 */
const withoutSoleCatchAll = (reasons: GroupedShowReason[]): GroupedShowReason[] =>
	reasons.length === 1 && reasons[0].filterParam === NO_REASON_FILTER_PARAM ? [] : reasons;

/**
 * The reason sub-rows of one bucket: the rows that explain it, labelled, biggest-first, with a
 * lone catch-all dropped. Shared with the per-source XLS Statistics modal (PREC-3870) so the two
 * surfaces break a bucket down the same way — they render the same counts, and a breakdown that
 * sorted or labelled differently in one of them would read as a different answer.
 */
export const buildGroupedShowReasons = (
	ts: (key: string) => string,
	inclusionStatus: string,
	exclusionReasonCounts: ExclusionReasonCount[] = []
): GroupedShowReason[] =>
	withoutSoleCatchAll(
		mergeAliases(
			exclusionReasonCounts
				.filter((row) => row.inclusionStatus === inclusionStatus)
				.map((row) => ({
					filterParam: canonicalReasonParam(row.filterParam),
					label: getReasonLabel(ts, row.inclusionStatus, canonicalReasonParam(row.filterParam)),
					count: toCount(row.count),
				}))
		).sort(byProminence)
	);

/**
 * One child per reason the user can tell apart (PREC-4520): reasons that read as one label are
 * summed into the child that stands for them, so the children still add up to the bucket.
 */
const mergeAliases = (reasons: GroupedShowReason[]): GroupedShowReason[] => {
	const merged = new Map<string, GroupedShowReason>();

	for (const reason of reasons) {
		const existing = merged.get(reason.filterParam);

		merged.set(
			reason.filterParam,
			existing ? { ...existing, count: existing.count + reason.count } : reason
		);
	}

	return [...merged.values()];
};

/**
 * View model for the grouped Show dropdown (PREC-3536 Phase 5): the four inclusion buckets
 * with lean counts, each carrying the per-reason sub-rows derived from
 * `list.exclusionReasonCounts`. All counts are coerced — SQL aggregates arrive as strings.
 *
 * PREC-3871: those sub-rows now partition their bucket — every lead is counted under exactly one
 * of them — so the children add up to the bucket count shown above them.
 */
export const getGroupedShowOptions = (
	ts: (key: string) => string,
	list?: List
): GroupedShowBucket[] =>
	getInclusionFilterOptions(ts, list).map((option) => ({
		status: option.id as InclusionFilterStatus,
		title: option.title,
		information: option.information,
		count: toCount(option.count),
		reasons: buildGroupedShowReasons(ts, option.id as string, list?.exclusionReasonCounts),
	}));

/**
 * Trigger label summary, mirroring the prototype: a fully selected bucket shows its name, a
 * partial one "Bucket · Reason" (or "Bucket · N reasons"); more than two parts collapse to
 * "N selected".
 */
export const buildShowLabel = (
	ts: (key: string) => string,
	selection: GroupedShowSelection,
	buckets: GroupedShowBucket[]
): string => {
	const parts: string[] = [];

	for (const bucket of buckets) {
		if (bucket.reasons.length === 0 || isBucketFullySelected(selection, bucket.status)) {
			if (selection.statuses.includes(bucket.status)) {
				parts.push(bucket.title);
			}

			continue;
		}

		const chosen = getSelectedReasons(
			selection,
			bucket.status,
			bucket.reasons.map((reason) => reason.filterParam)
		);

		if (chosen.length === 0) {
			continue;
		}

		if (chosen.length === 1) {
			const reason = bucket.reasons.find((r) => r.filterParam === chosen[0]);

			parts.push(`${bucket.title} · ${reason?.label ?? chosen[0]}`);
		} else {
			parts.push(`${bucket.title} · ${chosen.length} ${ts("showFilter.reasons")}`);
		}
	}

	if (parts.length === 0) {
		return "";
	}

	return parts.length <= 2 ? parts.join(", ") : `${parts.length} ${ts("showFilter.selected")}`;
};
